import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { XMarkIcon, SignalIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  font-family: ${theme.typography.fontFamily.primary};
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: ${theme.shadows['2xl']};
  display: flex;
  flex-direction: column;
  font-family: ${theme.typography.fontFamily.primary};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
`;

const ModalTitle = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily.primary};
  
  ${props => props.isOnline ? `
    background: ${theme.colors.success.main};
    color: ${theme.colors.success.contrast};
  ` : `
    background: ${theme.colors.neutral[600]};
    color: ${theme.colors.text.primary};
  `}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  transition: ${theme.transitions.default};
  
  &:hover {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const MapContainerStyled = styled.div`
  width: 100%;
  height: 500px;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid ${theme.colors.border.primary};
  overflow: hidden;
  position: relative;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
    border-radius: ${theme.borderRadius.lg};
  }
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const LocationInfo = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  font-family: ${theme.typography.fontFamily.primary};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily.primary};
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily.primary};
`;

const VesselLocationModal = ({ vessel, onClose }) => {
  const [location, setLocation] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (vessel) {
      fetchVesselLocation();
    }
  }, [vessel]);

  const fetchVesselLocation = async () => {
    try {
      setLoading(true);
      
      // Try to get current real-time location first
      const realtimeResponse = await api.get(`/api/vessels/${vessel.id}/signalk/data`);
      const signalkData = realtimeResponse.data;
      
      if (signalkData['navigation.position']) {
        const posData = signalkData['navigation.position'];
        setLocation({
          latitude: posData.value.latitude,
          longitude: posData.value.longitude,
          timestamp: posData.timestamp,
          isRealtime: true
        });
        setIsOnline(true);
      } else {
        // Fall back to last known position from database
        const lastPositionResponse = await api.get(`/api/vessels/${vessel.id}/last-position`);
        setLocation(lastPositionResponse.data);
        setIsOnline(false);
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      toast.error('Failed to load vessel location');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  if (!vessel) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {vessel.vessel_name} - Location
            <StatusBadge isOnline={isOnline}>
              {isOnline ? (
                <>
                  <SignalIcon className="h-4 w-4" />
                  Live
                </>
              ) : (
                <>
                  <SignalSlashIcon className="h-4 w-4" />
                  Last Known
                </>
              )}
            </StatusBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <MapPlaceholder>
              <div style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary }}>
                Loading location...
              </div>
            </MapPlaceholder>
          ) : location ? (
            <>
              <MapContainerStyled>
                <MapContainer 
                  center={[location.latitude, location.longitude]} 
                  zoom={15} 
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <TileLayer
                    attribution='&copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors'
                    url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
                  />
                  <Marker position={[location.latitude, location.longitude]}>
                    <Popup>
                      <strong>{vessel.vessel_name}</strong><br />
                      {isOnline ? '🟢 Live Position' : '📍 Last Known Position'}<br />
                      {location.timestamp && new Date(location.timestamp).toLocaleString()}
                    </Popup>
                  </Marker>
                </MapContainer>
              </MapContainerStyled>

              <LocationInfo>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Latitude</InfoLabel>
                    <InfoValue>{location.latitude.toFixed(6)}°</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Longitude</InfoLabel>
                    <InfoValue>{location.longitude.toFixed(6)}°</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Last Update</InfoLabel>
                    <InfoValue>
                      {location.timestamp ? new Date(location.timestamp).toLocaleString() : 'Unknown'}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Status</InfoLabel>
                    <InfoValue style={{ color: isOnline ? theme.colors.success.main : theme.colors.neutral[400] }}>
                      {isOnline ? 'Online (Live Data)' : 'Offline (Last Known)'}
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
              </LocationInfo>
            </>
          ) : (
            <MapPlaceholder>
              <div style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary }}>
                No location data available
              </div>
            </MapPlaceholder>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default VesselLocationModal;
